import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import {
  IUser,
  IListUserResponse,
  IListUsersRequestBody,
  IUserNamesResponse,
  IUserCreateResponse,
  IUserCreateRequest,
  IUserUpdateRequest,
  IUserUpdateResponse,
  IUserDeleteResponse,
  IUserStatusUpdateResponse,
  IActiveSuperVisorsResponse,
  IBulkUpdateRequest,
  IBulkResponse,
} from '../../state/models/user.model';
import { environment } from '../../../environments/environment';
import { IModulePermission } from 'src/app/state/models/role.model';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly userBaseUrl = `${environment.userEndpoint}/api/user`;

  constructor(
    private readonly apiService: ApiService,
    private readonly http: HttpClient
  ) {}

  createUser(user: IUserCreateRequest): Promise<IUserCreateResponse> {
    return this.apiService.post<IUserCreateResponse>(
      `${this.userBaseUrl}/`,
      user
    );
  }

  getUserById(id: number): Promise<{ data: IUser }> {
    return this.apiService.get<{ data: IUser }>(`${this.userBaseUrl}/${id}`);
  }

  listUsers(
    body: IListUsersRequestBody,
    page = 1,
    limit = 10
  ): Promise<IListUserResponse> {
    return this.apiService.post<IListUserResponse>(
      `${this.userBaseUrl}/list?page=${page}&limit=${limit}`,
      body
    );
  }

  listUserNames(): Promise<IUserNamesResponse> {
    return this.apiService.post<IUserNamesResponse>(
      `${this.userBaseUrl}/get-users-list`,
      {}
    );
  }

  exportUsers(
    body: IListUsersRequestBody,
    page = 1,
    limit = 10
  ): Promise<Blob> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    return this.apiService.postBlob<Blob>(
      `${this.userBaseUrl}/export`,
      body,
      params,
      undefined,
      { responseType: 'blob' as 'json' }
    );
  }

  listActiveSuperVisors(): Promise<IActiveSuperVisorsResponse> {
    return this.apiService.get<IActiveSuperVisorsResponse>(
      `${this.userBaseUrl}/users/active?is_supervisor=true`,
      {}
    );
  }

  // listAllSuperVisors(): Promise<IActiveSuperVisorsResponse> {
  //   return this.apiService.get<IActiveSuperVisorsResponse>(
  //     `${this.userBaseUrl}/users/all?is_supervisor=true`,
  //     {}
  //   );
  // }
  listAllSuperVisors(
    client_ids: number[],
    status: 'active' | 'inactive' | 'all',
    type: 'user' | 'supervisor'
  ): Promise<IActiveSuperVisorsResponse> {
    const body = {
      client_ids,
      status,
      type,
    };

    return this.apiService.post<IActiveSuperVisorsResponse>(
      `${this.userBaseUrl}/get-users`,
      body
    );
  }

  updateUser(
    id: number,
    user: IUserUpdateRequest
  ): Promise<IUserUpdateResponse> {
    return this.apiService.put<IUserUpdateResponse>(
      `${this.userBaseUrl}/${id}`,
      user
    );
  }
  updateRole(
    id: number,
    role: {
      role_id: string;
      permissions: IModulePermission[];
    }
  ): Promise<IUserUpdateResponse> {
    return this.apiService.post<IUserUpdateResponse>(
      `${this.userBaseUrl}/set-role/${id}`,
      { ...role }
    );
  }

  deleteUser(id: number): Promise<IUserDeleteResponse> {
    return this.apiService.delete<IUserDeleteResponse>(
      `${this.userBaseUrl}/${id}`
    );
  }

  changeUserStatus(id: number): Promise<IUserStatusUpdateResponse> {
    return this.apiService.patch<IUserStatusUpdateResponse>(
      `${this.userBaseUrl}/${id}`,
      {}
    );
  }

  importUsers(
    file: File,
    clientIds: string,
    programIds: string,
    enableIpBasedAccess: boolean
  ): Observable<IBulkResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/octet-stream',
    });
    const params = new HttpParams()
      .set('client_ids', clientIds)
      .set('program_ids', programIds)
      .set('enable_ip_based_access', String(enableIpBasedAccess));

    return this.http.post(`${this.userBaseUrl}/import`, file, {
      headers: headers,
      withCredentials: true,
      params: params,
      responseType: 'json',
    }) as Observable<IBulkResponse>;
  }

  exportErrorsTemplate(body: unknown): Promise<Blob> {
    const params = new HttpParams();

    return this.apiService.postBlob<Blob>(
      `${this.userBaseUrl}/export-failed`,
      body,
      params,
      undefined,
      { responseType: 'blob' as 'json' }
    );
  }

  downloadUsersTemplate(body: {
    client_ids?: number[];
    program_ids?: number[];
    status?: boolean;
    role_id?: number;
    supervisor_id?: number;
  }): Promise<Blob> {
    const params = new HttpParams();
    return this.apiService.postBlob<Blob>(
      `${this.userBaseUrl}/download-users-template`,
      body,
      params,
      undefined,
      { responseType: 'blob' as 'json' }
    );
  }

  bulkUpdateUsers(
    body: IBulkUpdateRequest,
    file: File
  ): Promise<IBulkResponse> {
    const headers = new Headers({
      'Content-Type': 'application/octet-stream',
    });
    const params = new HttpParams()
      .set('client_ids', body.client_ids)
      .set('program_ids', body.program_ids)
      .set('enable_ip_based_access', String(body.enable_ip_based_access));

    return this.apiService.postBlob<IBulkResponse>(
      `${this.userBaseUrl}/bulk-user-update`,
      file,
      params,
      headers,
      { responseType: 'json' }
    );
  }
}
